import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import { brand } from 'themes/brand';

export interface Item {
  id: string;
  name: string;
}

interface SearchableMultiSelectProps {
  items: Item[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  singularLabel?: string;
  pluralLabel?: string;
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  items,
  selectedIds,
  onChange,
  label = 'Selecionar',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'Nenhum item encontrado',
  singularLabel,
  pluralLabel,
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');

  const open = Boolean(anchorEl);
  const hasSelection = selectedIds.length > 0;
  const isFloating = open || hasSelection;

  const singular = singularLabel ?? label.toLowerCase();
  const plural = pluralLabel ?? `${label.toLowerCase()}s`;

  const triggerText =
    selectedIds.length === 0
      ? ''
      : selectedIds.length === 1
      ? `1 ${singular} selecionado`
      : `${selectedIds.length} ${plural} selecionados`;

  const filtered = useMemo(
    () =>
      items.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [items, search],
  );

  const handleOpen = () => setAnchorEl(triggerRef.current);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleToggle = (id: string) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id],
    );
  };

  const handleRemove = (id: string) =>
    onChange(selectedIds.filter((s) => s !== id));

  const handleClear = () => onChange([]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        ref={triggerRef}
        onClick={handleOpen}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          border: open
            ? `2px solid ${brand.primary}`
            : '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          padding: open ? '15.5px 13px' : '16.5px 14px',
          cursor: 'pointer',
          backgroundColor: '#fff',
          '&:hover': {
            borderColor: open ? brand.primary : 'rgba(0, 0, 0, 0.87)',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            position: 'absolute',
            left: isFloating ? '8px' : '14px',
            top: isFloating ? '-9px' : '50%',
            transform: isFloating ? 'none' : 'translateY(-50%)',
            fontSize: isFloating ? '12px' : '16px',
            color: open ? brand.primary : 'rgba(0, 0, 0, 0.6)',
            backgroundColor: '#fff',
            padding: '0 4px',
            transition: 'all 0.15s ease',
            pointerEvents: 'none',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            flex: 1,
            fontSize: '16px',
            color: hasSelection ? 'text.primary' : 'transparent',
            userSelect: 'none',
          }}
        >
          {triggerText || '​'}
        </Typography>

        <UnfoldMoreIcon
          sx={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '20px' }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: anchorEl?.offsetWidth ?? 300,
            maxHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            mt: 0.5,
            borderRadius: '4px',
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <List sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
          {filtered.map((item) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <ListItemButton
                key={item.id}
                onClick={() => handleToggle(item.id)}
                selected={isSelected}
                sx={{
                  py: 0.75,
                  px: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: 'primary.50',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {isSelected && <CheckIcon fontSize="small" color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: isSelected ? 500 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                  }}
                />
              </ListItemButton>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.disabled">
                {emptyMessage}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>

      {hasSelection && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mt: 1,
            alignItems: 'center',
          }}
        >
          {selectedIds.map((id) => {
            const item = items.find((i) => i.id === id);
            if (!item) return null;
            return (
              <Chip
                key={id}
                label={item.name}
                onDelete={() => handleRemove(id)}
                size="small"
                sx={{
                  borderRadius: '16px',
                  backgroundColor: '#f0f0f0',
                  color: 'text.secondary',
                  fontWeight: 400,
                  '& .MuiChip-deleteIcon': { color: 'rgba(0,0,0,0.4)' },
                }}
              />
            );
          })}
          <Button
            size="small"
            onClick={handleClear}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              minWidth: 'auto',
              px: 0.5,
            }}
          >
            Limpar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SearchableMultiSelect;
